import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import ImmutableUnderReviewFormControl from '@rd-web-markets/shared/dist/util/reviewEditControls/ImmutableUnderReviewFormControl';
import ProjectReportDateColumns from '@rd-web-markets/shared/dist/util/date/ProjectReportDateColumns';
import { SaveButton, DeleteButton } from '@rd-web-markets/shared/dist/util/buttons';
import claimGroupService from '@rd-web-markets/shared/dist/services/claim_group.service';
import {
  TEMPLATE_SELECT_MODEL_NAME,
  PROJECT_FIELD_MODEL_NAME,
  SHOW_PROJECT_TEMPLATE_SELECT,
  SHOW_KICK_OFF_DATES,
  SHOW_IT_TYPE_SELECT
} from '@rd-web-markets/market/dist/constants';
import TemplateCateogiresForCreatingProject from '@rd-web-markets/shared/dist/util/helpers/template_categories_for_createing_project';
import UnsavedChangesPrompt from '@rd-web-markets/shared/dist/components/UnsavedChangesPrompt';

const AddProjectForm = ({
  showProjectForm,
  setShowProjectForm,
  newReport,
  setNewReport,
  claimGroup,
  setClaimGroup,
  handleToaster,
  datePickerType,
  shouldShowSubsidiarySelect,
  subsidiaryCompanies,
  getData,
  params
}) => {
  const { t } = useTranslation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const updateClaimGroup = claimGroupService.useUpdateClaimGroup({ setClaimGroup });

  const changeReport = (e) => {
    setHasUnsavedChanges(true);
    setNewReport({
      ...newReport,
      [e.target.name]: e.target.value
    });
  };

  const setProjectTemplateForNewProject = (report, userSelectedProjectReportTemplate) => {
    // for all categories from the template - remove the id, so we create new ones for the new project
    userSelectedProjectReportTemplate.report_template_categories.forEach((category) => delete category.id);

    // Some fields are in the template, some fields are not - so here we add the missing text fields - Ro1, Ro2, etc.
    const reportTemplateWithAllCategories = TemplateCateogiresForCreatingProject(userSelectedProjectReportTemplate)

    reportTemplateWithAllCategories.report_template_categories.forEach((c) => {
      c.content = c.content || 'default_text';
    });

    setHasUnsavedChanges(true);
    setNewReport({
      ...report,
      template_name: userSelectedProjectReportTemplate.name,
      [PROJECT_FIELD_MODEL_NAME + '_attributes']: reportTemplateWithAllCategories?.report_template_categories,
    });

    claimGroup.selectedTemplate = claimGroup.report_template_project_specific?.name ? {} : reportTemplateWithAllCategories;
    setClaimGroup({ ...claimGroup });
  }

  const selectTechnicalTemplate = (e, report) => {
    const userSelectedProjectReportTemplate = claimGroup[TEMPLATE_SELECT_MODEL_NAME].find((t) => t.id === Number(e.target.value));
    if(userSelectedProjectReportTemplate) {
      setProjectTemplateForNewProject(report, userSelectedProjectReportTemplate);
    }else{
      setHasUnsavedChanges(true);
      setNewReport({
        ...newReport,
        template_name: ''
      })
    }
  };

  const createReport = async() => {
    await updateClaimGroup({ ...claimGroup, claim_project_reports_attributes: [newReport] });
    setHasUnsavedChanges(false);
    setShowProjectForm(false);
    await getData(params);
  };

  const handleCancel = () => {
    setHasUnsavedChanges(false);
    setShowProjectForm(false);
  };

  return (
    <>
      <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />

      <Row style={{display: showProjectForm ? 'block' : 'none'}} >
        <Col>
          <div className='table-responsive-responsive'>
            <table className='table table-hover'>
              <thead>
                <tr className='text-primary app-fs-table-header'>
                  <th>{t('project_name')}</th>
                  {shouldShowSubsidiarySelect && <th>{t('subsidiary_company')}</th>}
                  {SHOW_KICK_OFF_DATES &&  
                    <>
                      <th>{t('kick_off_project_w_customer')}</th>
                      <th>{t('bsfz_submission_target_date')}</th>
                    </>
                  }

                  <th>{t('rnd_project_start_date')}</th>
                  <th>{t('rnd_project_end_date')}</th>

                  { SHOW_PROJECT_TEMPLATE_SELECT &&  
                    <>
                      <th>{t('template_name')}</th>
                      <th>{t('technical_analysis_report')}</th>
                    </>
                  }
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <ImmutableUnderReviewFormControl
                      disabled={false}
                      name='project_name'
                      value={newReport.project_name}
                      onChange={changeReport}
                      />
                  </td>
                  {shouldShowSubsidiarySelect && (
                    <td>
                      <ImmutableUnderReviewFormControl
                        as="select"
                        name="subsidiary_company_id"
                        value={newReport.subsidiary_company_id ?? ''}
                        onChange={changeReport}
                        size="md"
                      >
                        <option value="">{t('select')}</option>
                        {subsidiaryCompanies.map((sc) => (
                          <option key={sc.id} value={sc.id}>
                            {sc.name}
                          </option>
                        ))}
                      </ImmutableUnderReviewFormControl>
                    </td>
                  )}
                  <ProjectReportDateColumns
                    datePickerType={datePickerType}
                    disabled={false}
                    report={newReport}
                    handleProjectReportChange={changeReport}
                    handleToaster={handleToaster}
                  />
                  {SHOW_IT_TYPE_SELECT && (
                    <td>
                      <ImmutableUnderReviewFormControl as='select' name='it_type' onChange={changeReport} size='md'>
                        {['default', 'not_it'].map((it_type) => {
                          return (
                            <option key={it_type + 'it_type_key'} value={it_type}>
                              {it_type === 'default' ? 'it' : 'not_it'}
                            </option>
                          );
                        })}
                      </ImmutableUnderReviewFormControl>
                    </td>
                  )}

                  { SHOW_PROJECT_TEMPLATE_SELECT &&
                    <>
                      <td>
                        <ImmutableUnderReviewFormControl as='select' name='selectedTemplate' onChange={(e) => selectTechnicalTemplate(e, newReport)} size='md'>
                          <option value='0'></option>
                          {claimGroup[TEMPLATE_SELECT_MODEL_NAME]?.map((template) => {
                            return (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            );
                          })}
                        </ImmutableUnderReviewFormControl>
                      </td>
                      <td>
                        {newReport.template_name}
                      </td>
                    </>
                  }
                  <td>
                    <SaveButton onClick={createReport}  disabled={shouldShowSubsidiarySelect && !newReport.subsidiary_company_id} />
                  </td>
                  <td>
                    <DeleteButton
                      style={{ marginRight: '8px' }}
                      onClick={handleCancel}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default AddProjectForm;

